import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import { Check, CheckBig, Clock, Info, ShieldCheck } from "../../components/icons.js";
import { ConsentWithdraw } from "./ConsentWithdraw.js";

/**
 * Candidate · done step + DPDP data rights (POST /invite/{token}/dsr). Shows what happens
 * next and the export/erase requests — never scores, never a decision. Erasure is a REQUEST:
 * our team handles it manually (we never auto-shred), so the copy promises a human follow-up,
 * not an instant deletion. It's still a two-step confirm because erasure is significant.
 *
 * The rights card also carries consent withdrawal (ConsentWithdraw) — it belongs beside export
 * and erase, and this is the page a candidate can always return to with the link they have.
 */

type DsrKind = "export" | "erase";

const DEFAULT_RETENTION_DAYS = 30;

/**
 * Short, stable reference code derived from the candidate's own capability token — purely a
 * convenience for support conversations ("what's your reference code?"), NOT a server-issued
 * tracking id (none exists). Deterministic so it's identical on every reload of this token.
 */
function referenceCode(token: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < token.length; i++) {
    h ^= token.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  const hex = (h >>> 0).toString(16).toUpperCase().padStart(8, "0");
  return `TH-${hex.slice(0, 4)}-${hex.slice(4)}`;
}

/**
 * `identityVerified` decides the FIRST line only, and it is the GENUINE persisted outcome — true
 * only when the check actually passed AND was not simulated. "Identity verified" is a statement of
 * fact, not a promise: asserting it for a demo/simulated run (or when the IDV adapter is off) tells
 * the candidate something untrue about their own record. Any other case — feature off, a demo run,
 * or a check that didn't pass — states only what actually happened: the interview was recorded and
 * stored in-region. We never surface a scores/decision claim here, only what is true of their run.
 */
const timeline = (identityVerified: boolean): { state: "done" | "now" | "next"; t: string; d: string }[] => [
  {
    state: "done",
    t: "Interview submitted",
    d: identityVerified
      ? "Identity verified · your responses recorded securely in-region."
      : "Your responses were recorded securely and stored in-region.",
  },
  {
    state: "now",
    t: "Your responses go to the recruiter",
    d: "A person at {org} reviews the shortlist and decides who moves forward.",
  },
  {
    state: "next",
    t: "You'll hear back — ideally with feedback",
    d: "Usually within a few days, by email, whatever the outcome.",
  },
];

export function DoneStep({
  token,
  orgName,
  requisitionTitle,
  candidateName,
  candidateEmail,
  identityVerified,
  retentionDays,
}: {
  token: string;
  orgName: string;
  requisitionTitle: string;
  candidateName: string | null;
  candidateEmail?: string | null;
  /** GENUINE outcome — real pass, not simulated. Only then do we claim "Identity verified". */
  identityVerified: boolean;
  /** Effective recording-retention window; falls back to the default until the API surfaces it. */
  retentionDays?: number;
}): ReactNode {
  const [confirming, setConfirming] = useState<DsrKind | null>(null);
  const [sent, setSent] = useState<Record<DsrKind, boolean>>({ export: false, erase: false });
  const [copied, setCopied] = useState(false);

  const dsr = useMutation({
    mutationFn: (kind: DsrKind) => api.post<unknown>(`/invite/${token}/dsr`, { kind }),
    onSuccess: (_data, kind) => {
      setSent((p) => ({ ...p, [kind]: true }));
      setConfirming(null);
    },
  });

  const firstName = candidateName ? (candidateName.split(" ")[0] ?? candidateName) : null;
  const retention = retentionDays ?? DEFAULT_RETENTION_DAYS;
  const refCode = referenceCode(token);

  return (
    <div className="cx-step">
      <div style={{ textAlign: "center", padding: "16px 0 4px" }}>
        <div className="done-check">
          <CheckBig />
        </div>
        <h1 className="t1c-h1">
          {firstName ? `Great job, ${firstName} — you're all set.` : "You're all set."}
        </h1>
        <p className="mut" style={{ maxWidth: 460, margin: "0 auto" }}>
          Your responses for <b>{requisitionTitle}</b> at <b>{orgName}</b> have been recorded and stored securely.
          Here's exactly what happens next.
        </p>
      </div>

      <div className="card pad" style={{ margin: "20px 0 16px", background: "var(--teal-wash)", border: "1px solid color-mix(in srgb, var(--teal) 25%, transparent)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div className="mut sm" style={{ fontWeight: 600 }}>
              Your reference code
            </div>
            <div style={{ fontFamily: "var(--mono)", fontWeight: 700, fontSize: 18, marginTop: 2 }}>{refCode}</div>
          </div>
          <button
            className="btn sm"
            onClick={() => {
              void navigator.clipboard?.writeText(refCode).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              });
            }}
          >
            {copied ? "Copied" : "Copy code"}
          </button>
        </div>
      </div>

      <div className="grid g3" style={{ marginBottom: 16 }}>
        <div className="card pad">
          <div className="mut sm" style={{ fontWeight: 600 }}>
            Target role
          </div>
          <div style={{ fontWeight: 650, marginTop: 4 }}>{requisitionTitle}</div>
          <div className="mut sm">{orgName}</div>
        </div>
        <div className="card pad">
          <div className="mut sm" style={{ fontWeight: 600 }}>
            Candidate contact
          </div>
          <div style={{ fontWeight: 650, marginTop: 4 }}>{candidateName ?? "—"}</div>
          {candidateEmail && (
            <div className="mut sm" style={{ marginTop: 2, wordBreak: "break-all" }}>
              {candidateEmail}
            </div>
          )}
        </div>
        <div className="card pad">
          <div className="mut sm" style={{ fontWeight: 600 }}>
            Storage
          </div>
          <div style={{ fontWeight: 650, marginTop: 4 }}>In-region · auto-deleted</div>
          <div className="mut sm">after {retention} days</div>
        </div>
      </div>

      <h3 className="t1c-h3" style={{ margin: "0 0 8px" }}>
        What happens next
      </h3>
      <div className="grid g3" style={{ marginBottom: 16 }}>
        {timeline(identityVerified).map((item) => (
          <div className="card pad" key={item.t}>
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                marginBottom: 8,
                background: item.state === "done" ? "var(--good-wash)" : item.state === "now" ? "var(--teal-wash)" : "var(--surface-2)",
                color: item.state === "done" ? "var(--good)" : item.state === "now" ? "var(--teal-ink)" : "var(--ink-mute)",
              }}
            >
              {item.state === "done" ? <Check /> : item.state === "now" ? <Clock /> : <Info />}
            </span>
            <div style={{ fontWeight: 600, fontSize: "13.6px" }}>{item.t}</div>
            <div className="mut sm">{item.d.replace("{org}", orgName)}</div>
          </div>
        ))}
      </div>

      <div className="banner info" style={{ marginBottom: 18 }}>
        <ShieldCheck />
        <div>
          <b>No one is ever rejected by a machine.</b> The engine ranks your interview and can suggest
          moving you forward — a person at {orgName} reviews that and decides. You don't need to do
          anything else right now.
        </div>
      </div>

      <div className="card">
        <div className="card-h">
          <h3>Your privacy & data rights</h3>
          <span className="pill n">DPDP</span>
        </div>
        <div className="card-b">
          <p className="mut sm" style={{ marginTop: 0 }}>
            These are your rights under India's DPDP Act — this is <b>not</b> a view of your interview results.
            Scores and rankings stay with the recruiter, and a person there decides.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid var(--line-soft)" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: "13.4px" }}>Request your data</div>
              <div className="mut sm">Get a copy of the personal data we hold about you.</div>
            </div>
            {sent.export ? (
              <span className="pill v">Request sent</span>
            ) : (
              <button className="btn sm" onClick={() => setConfirming("export")}>
                Request
              </button>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: "13.4px" }}>Erase your data</div>
              <div className="mut sm">
                Ask us to delete your recordings & identity data. Our team handles this manually on request.
              </div>
            </div>
            {sent.erase ? (
              <span className="pill v">Erasure requested</span>
            ) : (
              <button
                className="btn sm"
                style={{ color: "var(--crit)", borderColor: "var(--crit)" }}
                onClick={() => setConfirming("erase")}
              >
                Erase
              </button>
            )}
          </div>
          <ConsentWithdraw token={token} retentionDays={retention} />
          {dsr.isError && (
            <div className="banner crit" style={{ marginTop: 10 }}>
              <Info />
              <span>Couldn't send that request. Please retry in a moment.</span>
            </div>
          )}
        </div>
      </div>

      {confirming !== null && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "color-mix(in srgb, var(--frame) 55%, transparent)",
            backdropFilter: "blur(2px)",
            display: "grid",
            placeItems: "center",
            zIndex: 50,
          }}
          onClick={() => setConfirming(null)}
        >
          <div className="card pad" style={{ maxWidth: 420, margin: 16 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>
              {confirming === "export" ? "Request a copy of your data?" : "Request erasure of your data?"}
            </h3>
            <p className="mut sm">
              {confirming === "export"
                ? "We'll prepare an export of the personal data held about you and send it to your contact details on file."
                : "We'll pass your request to our team, who will erase your recordings and identity data. This is handled manually — it isn't instant, and we may contact you to confirm."}
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button className="btn ghost sm" onClick={() => setConfirming(null)}>
                Cancel
              </button>
              <button
                className={`btn sm ${confirming === "erase" ? "danger-solid" : "primary"}`}
                disabled={dsr.isPending}
                onClick={() => dsr.mutate(confirming)}
              >
                {dsr.isPending
                  ? "Sending…"
                  : confirming === "export"
                    ? "Send request"
                    : "Send erasure request"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
